import { AvField } from 'availity-reactstrap-validation';
import DatePicker from 'react-datepicker';
import * as datePickerLocales from 'date-fns/locale';
import Select from 'react-select';
import { Col, Label } from 'reactstrap';
import { INTERNATIONAL_DATE_FORMAT } from 'src/components/constants';
import {
    getNestedValue,
    getSubTypeOptions,
    THIRD_PARTY_TYPE_OPTIONS,
} from '../constants/fieldDefinitions';
import DescriptionEditorField from './DescriptionEditorField';

const DynamicFormField = ({
    field,
    values,
    t,
    lng,
    countriesList,
    onChange,
}) => {
    const value = getNestedValue(values, field.key);
    const fieldId = field.key.replace(/\./g, '_');

    const renderSelectOptions = () => {
        if (field.type === 'thirdPartyType') {
            return THIRD_PARTY_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                    {t(option.label)}
                </option>
            ));
        }

        if (field.type === 'companySize') {
            return (field.options || []).map((option) => (
                <option key={option.value} value={option.value}>
                    {option.label} {t('Employees')}
                </option>
            ));
        }

        if (field.type === 'subType') {
            return getSubTypeOptions(values.type).map((option) => (
                <option key={option.value} value={option.value}>
                    {t(option.label)}
                </option>
            ));
        }

        return null;
    };

    const commonValidation = {
        required: {
            value: !!field.required,
        },
    };

    if (field.type === 'richText') {
        return (
            <DescriptionEditorField
                key={field.key}
                t={t}
                labelKey={field.labelKey}
                editorState={value}
                onChange={(editorState) => onChange(field.key, editorState)}
                required={field.required}
            />
        );
    }

    if (field.type === 'country') {
        const isReadOnly = !!field.readOnly;

        return (
            <Col lg="3" md="6" sm="12" className="mb-4" key={field.key}>
                <Label for={fieldId}>
                    {t(field.labelKey)}:{field.required && !isReadOnly && <span className="text-danger"> *</span>}
                </Label>
                <Select
                    inputId={fieldId}
                    placeholder={t('Select...')}
                    classNamePrefix="select2-selection"
                    options={countriesList}
                    menuPortalTarget={document.body}
                    isDisabled={isReadOnly}
                    onChange={(selected) => {
                        if (!isReadOnly) {
                            onChange(field.key, selected);
                        }
                    }}
                    value={value}
                />
                {!isReadOnly && (
                    <AvField
                        name={field.key}
                        type="hidden"
                        validate={commonValidation}
                        errorMessage={t('This field cannot be blank')}
                        value={value}
                    />
                )}
            </Col>
        );
    }

    if (field.type === 'date') {
        return (
            <Col lg="3" md="6" sm="12" className="mb-4" key={field.key}>
                <Label for={fieldId}>
                    {t(field.labelKey)}:{field.required && <span className="text-danger"> *</span>}
                </Label>
                <DatePicker
                    locale={datePickerLocales[lng]}
                    id={fieldId}
                    className="form-control"
                    selected={value ? new Date(value) : null}
                    showTimeSelect={false}
                    dateFormat={INTERNATIONAL_DATE_FORMAT}
                    onChange={(date) => onChange(field.key, date)}
                />
                <AvField
                    name={field.key}
                    type="hidden"
                    validate={commonValidation}
                    errorMessage={t('This field cannot be blank')}
                    value={value}
                />
            </Col>
        );
    }

    if (['thirdPartyType', 'subType', 'companySize'].includes(field.type)) {
        return (
            <Col lg="3" md="6" sm="12" className="mb-4" key={field.key}>
                <Label for={fieldId}>
                    {t(field.labelKey)}:{field.required && <span className="text-danger"> *</span>}
                </Label>
                <AvField
                    id={fieldId}
                    name={field.key}
                    type="select"
                    className="form-control"
                    validate={commonValidation}
                    errorMessage={t('This field cannot be blank')}
                    value={value || ''}
                    onChange={(event) => onChange(field.key, event.target.value)}
                >
                    <option value="">{t('Select...')}</option>
                    {renderSelectOptions()}
                </AvField>
            </Col>
        );
    }

    const validation = field.type === 'url'
        ? {
            ...commonValidation,
            pattern: {
                value: /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/\S*)?$/,
                errorMessage: t('Please enter a valid URL'),
            },
        }
        : commonValidation;

    return (
        <Col lg="3" md="6" sm="12" className="mb-4" key={field.key}>
            <Label for={fieldId}>
                {t(field.labelKey)}:{field.required && <span className="text-danger"> *</span>}
            </Label>
            <AvField
                id={fieldId}
                name={field.key}
                type={field.type === 'url' ? 'text' : field.type}
                className="form-control"
                validate={validation}
                errorMessage={t('This field cannot be blank')}
                value={value || ''}
                onChange={(event) => onChange(field.key, event.target.value)}
            />
        </Col>
    );
};

export default DynamicFormField;
